import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { ArrowBack, Edit, Delete } from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { mobileBeneficiaryService } from '../../../services/mobile/mobileBeneficiaryService';

const MobileBeneficiaryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [beneficiary, setBeneficiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchBeneficiary = async () => {
      setLoading(true);
      try {
        const result = await mobileBeneficiaryService.getBeneficiaryById(id);
        if (result.success) {
          setBeneficiary(result.data || null);
        }
      } catch {
        // handled by service layer
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchBeneficiary();
  }, [id]);

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const result = await mobileBeneficiaryService.deleteBeneficiary(id);
      if (result.success) {
        navigate('/mobile/beneficiaries');
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to delete beneficiary.', severity: 'error' });
        setDeleteOpen(false);
      }
    } catch {
      setSnackbar({ open: true, message: 'An error occurred.', severity: 'error' });
      setDeleteOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  if (!beneficiary) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="body1" color="text.secondary">Beneficiary not found.</Typography>
        <EasyCheckButton onClick={() => navigate('/mobile/beneficiaries')} sx={{ mt: 2, color: '#2563eb' }}>
          Back to Beneficiaries
        </EasyCheckButton>
      </Box>
    );
  }

  const isActive = beneficiary.IsActive !== undefined ? beneficiary.IsActive : beneficiary.isActive;

  const details = [
    { label: 'Beneficiary Name', value: beneficiary.BeneficiaryName || beneficiary.beneficiaryName },
    { label: 'Status', value: isActive ? 'Active' : 'Inactive' },
    { label: 'Created By', value: beneficiary.CreatedUser || beneficiary.createdUser },
    { label: 'Created Date', value: formatDate(beneficiary.CreatedDate || beneficiary.createdDate) },
    { label: 'Modified By', value: beneficiary.ModifiedUser || beneficiary.modifiedUser || '-' },
    { label: 'Modified Date', value: formatDate(beneficiary.ModifiedDate || beneficiary.modifiedDate) }
  ];

  return (
    <Box>
      {/* Back Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/mobile/beneficiaries')} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', flex: 1 }}>
          Beneficiary Details
        </Typography>
        <Chip
          label={isActive ? 'Active' : 'Inactive'}
          color={isActive ? 'success' : 'default'}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Details Card */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          {details.map((item, index) => (
            <Box key={item.label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: '#0f172a',
                    textAlign: 'right',
                    maxWidth: '60%',
                    wordBreak: 'break-word'
                  }}
                >
                  {item.value || '-'}
                </Typography>
              </Box>
              {index < details.length - 1 && <Divider />}
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <EasyCheckButton
          fullWidth
          onClick={() => navigate(`/mobile/beneficiaries/${id}/edit`)}
          startIcon={<Edit />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            backgroundColor: '#2563eb',
            '&:hover': { backgroundColor: '#1d4ed8' },
            fontWeight: 600
          }}
        >
          Edit Beneficiary
        </EasyCheckButton>

        <EasyCheckButton
          fullWidth
          onClick={() => setDeleteOpen(true)}
          disabled={actionLoading}
          startIcon={<Delete />}
          variant="outlined"
          sx={{
            py: 1.5,
            borderRadius: 2,
            color: '#ef4444',
            borderColor: '#ef4444',
            '&:hover': { borderColor: '#dc2626', backgroundColor: '#fef2f2' },
            fontWeight: 600
          }}
        >
          Delete Beneficiary
        </EasyCheckButton>
      </Box>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete Beneficiary</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Are you sure you want to delete this beneficiary? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <EasyCheckButton onClick={() => setDeleteOpen(false)} sx={{ color: '#64748b' }}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            onClick={handleDelete}
            disabled={actionLoading}
            sx={{ color: '#ef4444', fontWeight: 600 }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MobileBeneficiaryDetail;
